import { MsalProvider } from "@azure/msal-react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { registerSW } from "virtual:pwa-register";
import App from "./App";
import ErrorBoundary from "./components/ErrorBoundary";
import { AuthProvider } from "./contexts/AuthContext";
import { SyncProvider } from "./contexts/SyncContext";
import { ToastProvider } from "./contexts/ToastContext";
import "./index.css";
import { clientId, msalInstance } from "./services/authConfig";

// Configuration du QueryClient
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes
      gcTime: 1000 * 60 * 10, // 10 minutes (anciennement cacheTime)
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

// Sans stockage persistant, Android peut évincer l'IndexedDB entière — et donc
// la file de photos — sous pression de disque, sans avertissement.
//
// Mesuré le 2026-08-21 sur le téléphone de production : `persisted()` y renvoie
// `true`, Chrome accorde donc la persistance sans invite et cet appel fait son
// travail. Sept jours de photos y pèsent ~54 Mo (~75 Mo au plafond de capture de
// l'appareil) pour un quota de 32,4 Go — d'où l'absence d'alerte d'espace dans
// l'interface : elle serait une inquiétude sans objet. La persistance ne se perd
// qu'en vidant les données du site à la main, geste qui emporte du même coup les
// sept jours de photos (voir docs/debogage-a-distance.md).
navigator.storage?.persist?.();

const root = createRoot(document.getElementById("root")!);

// Sans identifiant client, l'application ne démarre pas : elle le dit et
// s'arrête là. La laisser démarrer donnerait un bouton de connexion qui part
// en OAuth à vide et coince le localStorage pour toutes les fois suivantes.
if (!clientId) {
  root.render(
    <main style={{ fontFamily: "system-ui", lineHeight: 1.5, padding: "2rem" }}>
      <h1>Configuration absente</h1>
      <p>
        <code>VITE_AZURE_CLIENT_ID</code> n'est pas défini : l'application ne
        peut pas s'authentifier, elle ne démarre donc pas.
      </p>
      <p>
        Copier <code>.env.local.template</code> en <code>.env.local</code>, y
        placer l'identifiant d'inscription applicative Azure, puis relancer le
        serveur de développement.
      </p>
    </main>
  );
  throw new Error("VITE_AZURE_CLIENT_ID est absent : voir .env.local.template");
}

// Initialize MSAL before rendering
msalInstance.initialize().then(() => {
  // Register Service Worker
  registerSW({ immediate: true });

  root.render(
    <StrictMode>
      <ErrorBoundary>
        <QueryClientProvider client={queryClient}>
          <MsalProvider instance={msalInstance}>
            <AuthProvider>
              <SyncProvider>
                <ToastProvider>
                  <App />
                </ToastProvider>
              </SyncProvider>
            </AuthProvider>
          </MsalProvider>
        </QueryClientProvider>
      </ErrorBoundary>
    </StrictMode>
  );
});
